<template>
  <div
    class="flex flex-col justify-center items-center py-8 gap-5 w-full max-w-md"
  >
    <div class="tour-detect-input flex justify-center items-end w-full">
      <div class="form-control w-full">
        <label class="label pl-3">
          <span class="label-text"
            >Firmware filename / Build ID / Firmware hash ({{
              HASH_NAME
            }})</span
          >
        </label>

        <input
          type="text"
          placeholder="e.g. 'ghanima-1234567-8.bin' or '8' or '8db45a225b2fb8499c63f6525da3170ed6c31252c4e0096055edecf989fafd29'"
          v-model="input"
          class="input input-bordered w-full"
        />
      </div>
    </div>

    <div class="join">
      <div
        class="tour-detect-upload tooltip"
        data-tip="Compute firmware hash from uploaded firmware file"
      >
        <FileUpload
          accept=".bin"
          method="readAsArrayBuffer"
          @loaded="setHashFromFile"
          class="join-item"
        >
          <Icon icon="ic:baseline-upload" class="text-lg" />
          Upload file
        </FileUpload>
      </div>

      <div
        class="tour-detect-load tooltip"
        data-tip="Load detected configuration"
      >
        <button
          class="btn btn-accent join-item"
          :disabled="jobInfo == null || configLoadOngoing"
          @click="loadConfig"
        >
          Load config
        </button>
      </div>
    </div>

    <div class="form-control w-full">
      <label class="label pl-2">
        <span class="label-text">Detection result:</span>
      </label>

      <div class="overflow-x-auto">
        <table v-if="jobInfo != null" class="table">
          <tbody>
            <tr v-for="(row, i) in jobInfoTable" :key="i">
              <th>{{ row[0] }}</th>
              <td>{{ row[1] }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else-if="pendingDebounce || pendingFetch" class="loading"></div>
        <div v-else>Not found or is private to other user</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from "vue";
import { useToast } from "vue-toastification";
import { BuildJobShort } from "@/generated/api";
import { fs, assertUnreachable } from "@/utils";
import { useBackendStore, useKeyboardStore } from "@/stores";
import { BUILD_STATUS } from "@/stores/firmware";
import FileUpload from "@/components/utils/FileUpload.vue";

const HASH_NAME = "SHA-256";
const HASH_HEX_LEN = (256 / 8) * 2;
const GET_BY_HASH_DEBOUNCE_MS = 500;

const backend = useBackendStore();
const kb = useKeyboardStore();
const toast = useToast();

onMounted(async () => {
  await backend.fetchBuildJobList();
});

const input = ref("");
const jobInfo = ref<BuildJobShort | null>(null);
const pendingDebounce = ref<number | null>(null);
const pendingFetch = ref(false);
const configLoadOngoing = ref(false);

const parsedInput = computed(() => parseInput(input.value));

const configShort = computed(() => {
  const configId = jobInfo.value?.config;
  if (configId == null) return null;
  return backend.configList.find((c) => c.id == configId);
});

const jobInfoTable = computed(() => {
  const job = jobInfo.value;
  if (!job) return null;
  return [
    ["Build ID", job.id.toString()],
    ["Status", (job.status && BUILD_STATUS[job.status]) ?? job.status],
    [
      "Date",
      new Date(
        Date.parse(job.date_finished ?? job.date_started ?? job.date_created)
      ).toLocaleString(),
    ],
    [
      "Firmware revision",
      job.firmware_revision ? job.firmware_revision.substring(0, 7) : "",
    ],
    ["Configuration", configShort.value?.name ?? "-"],
    ["Owner", configShort.value?.owner_name ?? "-"],
  ];
});

type ParsedFileName = {
  board: string;
  rev: string;
  build_id: number;
};
type ParsedBuildId = { build_id: number };
type ParsedHash = { hash: string };
type ParseInputResult = ParsedFileName | ParsedBuildId | ParsedHash | null;

const parseInput = (text: string): ParseInputResult => {
  const num = Number(text);
  if (Number.isInteger(num)) {
    return { build_id: num };
  }

  const hash_match = text.match(/^\s*([a-fA-F0-9]+)\s*$/);
  if (hash_match?.[0].length == HASH_HEX_LEN) {
    return { hash: hash_match[1] };
  }

  const [head, suffix] = fs.splitExt(text);
  if (suffix.length > 0 && suffix != ".bin") return null;
  const basename = fs.baseName(head);

  const parts = basename.split("-");
  if (parts.length != 3) {
    return null;
  }
  const build_id = parts[2].length > 0 ? Number(parts[2]) : NaN;
  if (!Number.isInteger(build_id)) return null;

  return { board: parts[0], rev: parts[1], build_id };
};

const getByHashStop = () => {
  clearTimeout(pendingDebounce.value ?? undefined);
  pendingDebounce.value = null;
  pendingFetch.value = false;
};

const getByHash = (hash: string) => {
  const job = backend.buildJobsByResultsHashCache.get(hash)?.[0];
  if (job) {
    jobInfo.value = job;
    return;
  }

  getByHashStop();

  pendingDebounce.value = setTimeout(async () => {
    pendingFetch.value = true;
    const jobs = await backend.fetchBuildJobsByResultsHash(hash);

    if (pendingFetch.value) {
      pendingFetch.value = false;
      jobInfo.value = jobs[0];
    }

    getByHashStop();
  }, GET_BY_HASH_DEBOUNCE_MS);
};

const getJobInfo = async (id?: number) => {
  if (id == null) {
    jobInfo.value = null;
    return;
  }
  jobInfo.value = backend.buildJobList.find((job) => job.id == id) ?? null;
};

watch(
  parsedInput,
  async (newInput: ParseInputResult, _oldInput: ParseInputResult) => {
    getByHashStop();

    if (newInput == null) {
      jobInfo.value = null;
      return;
    }

    if ("build_id" in newInput) {
      getJobInfo(newInput.build_id);
    } else if ("hash" in newInput) {
      getByHash(newInput.hash);
    } else {
      assertUnreachable(newInput);
    }
  }
);

const loadConfig = async () => {
  const id = jobInfo.value?.id;
  if (id == undefined) return;

  try {
    configLoadOngoing.value = true;

    const job = await backend.getFullBuildJob(id);
    if (!job.config_snapshot) return;

    kb.setConfig(job.config_snapshot);
    if (backend.currentConfig != null) backend.currentConfig.name = input.value;
    toast.success("Loaded configuration from snapshot");
  } finally {
    configLoadOngoing.value = false;
  }
};

const setHashFromFile = async (_file: File, data: ArrayBuffer | string) => {
  if (!crypto?.subtle?.digest) {
    toast.error("Not supported in this browser");
    return;
  }

  const hash_buf = await crypto.subtle.digest(HASH_NAME, data as ArrayBuffer);
  const hash_u8 = new Uint8Array(hash_buf);
  const hash_hexstr = Array.from(hash_u8)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  input.value = hash_hexstr;
};
</script>
